import React from 'react';
import { useDataStore } from 'files/StateManagement.js';

import Abstract from 'classes/Abstract.js';
import Account from 'classes/Account.js';
import { AddEditAccount, AddEditInstruction, AddEditTrustedContact, ConfigureTriggers } from 'managers/Users.js';
import { AddEditDataVaultItem } from 'managers/DataVault.js';
import Appearance from 'styles/Appearance.js';
import { AsyncInteractionItem } from 'managers/Components.js';
import DataVaultItem from 'classes/DataVaultItem.js';
import Instruction from 'classes/Instruction.js';
import { LayerItem } from 'structure/Layer.js';

const ActionSteps = ({ description, exclude = [], lastItem = true, onClick, style, title, utils }) => {

    const { user } = useDataStore();

    const onAddLinkedAccount = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('accounts', 'create');

            // close parent if applicable
            if(typeof(onClick) === 'function') {
                onClick();
            }

            // show layer with options to add an account
            utils.layer.open({
                abstract: Abstract.create({
                    object: Account.new(),
                    type: 'accounts'
                }),
                Component: AddEditAccount.bind(this, { isNewTarget: true }),
                id: 'new_linked_account'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onCreateTrustedContact = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('trusted_contacts', 'create');

            // close parent if applicable
            if(typeof(onClick) === 'function') {
                onClick();
            }

            // show layer with options to create a trusted contact
            utils.layer.open({
                abstract: Abstract.create({
                    object: {},
                    type: 'trusted_contacts'
                }),
                Component: AddEditTrustedContact.bind(this, { 
                    isNewTarget: true
                }),
                id: 'new_trusted_contact'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onCreateTriggerInstruction = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('trigger_instructions', 'create');

            // close parent if applicable
            if(typeof(onClick) === 'function') {
                onClick();
            }

            // show layer with options to create a trigger instruction
            utils.layer.open({
                abstract: Abstract.create({
                    object: Instruction.new(),
                    type: 'trigger_instructions'
                }),
                Component: AddEditInstruction.bind(this, { 
                    isNewTarget: true
                }),
                id: 'new_trigger_instruction'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onSetupEmergencyTrigger = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('emergency_trigger', 'create');

            // close parent if applicable
            if(typeof(onClick) === 'function') {
                onClick();
            }

            // show layer with options to configure emergency trigger
            utils.layer.open({
                abstract: Abstract.create({
                    object: user.subscription,
                    type: 'subscriptions'
                }),
                Component: ConfigureTriggers.bind(this, { category: 'emergency_triggers' }),
                id: `emergency_triggers.${user.subscription.id}`
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onSetupLifeChecks = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('life_checks', 'create');

            // close parent if applicable
            if(typeof(onClick) === 'function') {
                onClick();
            }

            // show layer with options to configure life checks
            utils.layer.open({
                abstract: Abstract.create({
                    object: user.subscription,
                    type: 'subscriptions'
                }),
                Component: ConfigureTriggers.bind(this, { category: 'life_checks' }),
                id: `life_checks.${user.subscription.id}`
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onUploadDataVaultItem = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('data_vault', 'create');

            // close parent if applicable
            if(typeof(onClick) === 'function') {
                onClick();
            }

            // show layer with options to add an account
            utils.layer.open({
                abstract: Abstract.create({
                    object: DataVaultItem.new(),
                    type: 'data_vault_items'
                }),
                Component: AddEditDataVaultItem.bind(this, { isNewTarget: true }),
                id: 'new_data_vault_item'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const getItems = () => {
        return [{
            description: `Securely store your important logins and passwords in LifeWipe’s free password manager.`,
            id: 'create_linked_account',
            image: window.theme === 'dark' ? 'images/requiem-action-linked-accounts-dark.png' : 'images/requiem-action-linked-accounts-light.png',
            onClick: onAddLinkedAccount,
            title: 'Add Account to Locket',
        },{
            description: 'Designate someone you trust to carry out your wishes if you’re ever unreachable.',
            id: 'create_trusted_contact',
            image: window.theme === 'dark' ? 'images/requiem-action-trusted-contacts-dark.png' : 'images/requiem-action-trusted-contacts-light.png',
            onClick: onCreateTrustedContact,
            title: 'Add a Trusted Contact',
        },{
            description: 'Set clear actions for what should happen to your data if something happens to you.',
            id: 'create_trigger_instruction',
            image: window.theme === 'dark' ? 'images/requiem-action-instructions-dark.png' : 'images/requiem-action-instructions-light.png',
            onClick: onCreateTriggerInstruction,
            title: 'Create a Trigger Instruction',
        },{
            description: 'Safely store critical documents, notes, or messages in your encrypted vault.',
            id: 'create_data_vault_item',
            image: window.theme === 'dark' ? 'images/requiem-action-data-vault-dark.png' : 'images/requiem-action-data-vault-light.png',
            onClick: onUploadDataVaultItem,
            title: 'Upload File to Data Vault',
        },{
            description: "Enable periodic check-ins to confirm you're okay — or silently escalate if you're not.",
            id: 'setup_lifechecks',
            image: window.theme === 'dark' ? 'images/requiem-action-life-checks-dark.png' : 'images/requiem-action-life-checks-light.png',
            onClick: onSetupLifeChecks,
            title: 'Set Up LifeChecks',
        },{
            description: 'Configure a button, phrase, or biometric action that instantly activates your protection protocols.',
            id: 'setup_emergency_trigger',
            image: window.theme === 'dark' ? 'images/requiem-action-emergency-trigger-dark.png' : 'images/requiem-action-emergency-trigger-light.png',
            onClick: onSetupEmergencyTrigger,
            title: 'Set Up Emergency Trigger'
        }].filter(item => {
            return exclude?.includes(item.id) === true ? false : true;
        });
    }

    return (
        <LayerItem 
        collapsed={false}
        lastItem={lastItem}
        title={title || `What Should I Do Next?`}
        style={style}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>{description || `Below are a few powerful ways you can help strengthen your setup. Every action you take here helps ensure your data, identity, and wishes are protected — no matter what happens.`}</span>
                {getItems().map((item, index, items) => {
                    return (
                        <AsyncInteractionItem 
                        {...item}
                        imageStyle={{
                            borderRadius: 0
                        }}
                        key={index}
                        lastItem={index === items.length - 1}
                        utils={utils} />
                    )  
                })}
            </div>
        </LayerItem>
    )
}

export default ActionSteps;